import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import { PrimengButtonDirective, SpinnerFilledComponent } from '@web/shared/ui';
import {
  ActivatedRoute,
  Params,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { EMPTY, map, Observable, switchMap } from 'rxjs';
import { isNil, Nillable } from '@web/utils';
import {
  activatedRouteQueryParamAsString,
  NavigatorService,
  RouteQueryParam,
} from '@web/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PublicBrandCategory,
  PublicBrandDataSourceService,
} from '@web/shared/module/brand';

@Component({
  selector: 'app-public-brand-categories',
  imports: [
    CommonModule,
    Button,
    PrimengButtonDirective,
    RouterLink,
    RouterLinkActive,
    SpinnerFilledComponent,
  ],
  templateUrl: './public-brand-categories.component.html',
  styleUrl: './public-brand-categories.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicBrandCategoriesComponent implements OnInit {
  private readonly brandPublicDataSource = inject(PublicBrandDataSourceService);
  private readonly navigator = inject(NavigatorService);

  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly currentCategory$ = this.currentCategorySource();
  protected readonly categories$ = this.categoriesSource();

  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.navigateToDefaultCategory();
  }

  protected categoryQueryParam(categoryName: string | 'all'): Params {
    return {
      [RouteQueryParam.PL_Category]: categoryName,
    };
  }

  private currentCategorySource(): Observable<Nillable<string>> {
    return activatedRouteQueryParamAsString(RouteQueryParam.PL_Category);
  }

  private categoriesSource(): Observable<PublicBrandCategory[]> {
    return this.brandPublicDataSource.fetchCategories();
  }

  private navigateToDefaultCategory(): void {
    this.currentCategory$
      .pipe(
        switchMap((category) =>
          isNil(category)
            ? this.categories$.pipe(map(([{ name }]) => name))
            : EMPTY,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((name) => {
        this.navigator.changeQueryParams(this.activatedRoute, {
          queryParams: this.categoryQueryParam(name),
        });
      });
  }
}
